import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient, HttpContext } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { SanitizePipe, isDefined, takeOnce } from '@partners/utils';
import { tap } from 'rxjs';
import {
  TAssetFileExt,
  TAssetFlow,
  TAssetSize,
  TAssetSizeExpression,
  TAssetType,
  TIconSet
} from '../../model/theme.types';
import { ThemeService } from '../../services';
import { THEMES_CONFIG, THEME_CONTEXT } from '../../tokens';

@Component({
  standalone: true,
  selector: 'theme-asset',
  template: `
    @if(loadViaHttp()) { @defer (when assetContent$$()) {
    <span [@toggleIcon] class="theme-asset__content" [innerHTML]="assetContent$$() | sanitize: 'html'"></span>
    } } @else {
    <img class="theme-asset__image" [src]="path()" />
    }
  `,
  imports: [SanitizePipe],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'theme-asset'
  },
  animations: [
    trigger('toggleIcon', [
      transition(':enter', [style({ opacity: 0 }), animate('250ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('250ms', style({ opacity: 0 }))])
    ])
  ]
})
export class ThemeAssetComponent implements OnInit /*, OnDestroy */ {
  /**
   * use this option to pass in a relative asset path => asset name only: ('cal-logo | cal-logo.svg')
   * */
  asset = input<string>();

  /**
   * use this option to pass in an absolute asset path => path starting at root folder name up to file
   * extension ('./assets/cal-logo.svg')
   * */
  exactPath = input<string>();

  /** filesystem partition (folder) of the asset ('/img/' | '/svg/' ets') */
  partition = input<TAssetType>('img');

  extension = input<TAssetFileExt>('svg');

  size = input<TAssetSize>('auto');

  iconSet = input<TIconSet | undefined>(undefined);

  /**
   * source property tells us which assets folder the current asset should be pulled for display
   * when dynamically building the path. there are 2 options:
   * (1) theme - default option, pulls assets from the current theme's assets library.
   * (2) partner - pulls assets from the partner's assets library.
   * normally - the two values are the same, the theme name is in fact the partner's name, however,
   * in some cases they may differ and when they do - we still need to maintain the ability to pull
   * specific assets from the PARTNER assets library and not from theme examples: partner's logo will
   * almost always be pulled from partners folder
   **/
  source = input<'partner' | 'theme'>('theme');

  /** limit the maximum width of the asset, otherwise it takes the width of its container */
  maxWidth = input<TAssetSizeExpression>();

  /** when true - asset is loaded via http/get request and injected into the dom as raw element (<svg...) */
  loadViaHttp = input<boolean>(false);

  flow = input<TAssetFlow>('inline-flex');

  @HostBinding('class')
  get responsiveSize() {
    return this.size() !== 'auto' ? this.size() : null;
  }

  @HostBinding('style.width')
  get autoSize() {
    return this.size() === 'auto' ? '100%' : this.size();
  }

  @HostBinding('style.maxWidth')
  get maxWidthValue() {
    const maxWidth = computed<string>(() => `${this.maxWidth()}`);
    return maxWidth() ?? null;
  }

  get themeRoot(): string {
    return this.#themeConfig.themeRoot;
  }

  get themeIcons(): string {
    return this.#themeConfig.themeIconSet;
  }

  assetContent$$ = signal('');

  #httpService = inject(HttpClient);
  #themeService = inject(ThemeService);
  #themeConfig = inject(THEMES_CONFIG);

  path = computed<string>(() => {
    const { themeName, themeSource } = this.#themeService.theme();

    if (this.exactPath()) {
      return `${this.exactPath()}`;
    } else {
      const source = this.source() === 'partner' ? themeSource : themeName;
      const iconSet = this.iconSet();
      if (isDefined(iconSet) && iconSet !== 'custom') {
        return `${this.themeRoot}/${this.themeIcons}/${this.iconSet()}/${this.asset()}.${this.extension()}`;
      }

      return `${this.themeRoot}/${source}/${this.partition()}/${this.asset()}.${this.extension()}`;
    }
  });

  constructor() {}

  ngOnInit(): void {
    const useHttp = this.loadViaHttp();
    if (!useHttp) {
      return;
    }
    this.#loadDynamically();
  }

  #loadDynamically() {
    const assetPath = this.path();

    // console.log(`load asset via http: "${assetPath}"`);

    this.#httpService
      .get(`${assetPath}`, {
        context: new HttpContext().set(THEME_CONTEXT, { loader: { toggle: false } }),
        responseType: 'text'
      })
      .pipe(
        takeOnce(),
        tap((response) => {
          this.assetContent$$.set(response as string);
        })
      )
      .subscribe();
  }
}
